import { PasswordSchema } from "@adpilot/shared";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { changePassword } from "@/lib/account-api";
import { getApiErrorMessage } from "@/lib/api-error";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** 修改密码(ADP-167,Xnurta 对齐:原密码/新密码/新密码确认)。 */
export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const { t } = useTranslation("account");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError(null);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => changePassword(oldPassword, newPassword),
    onSuccess: () => {
      toast.success(t("changePassword.success"));
      onOpenChange(false);
    },
    onError: async (mutationError) => {
      setError(await getApiErrorMessage(mutationError));
    },
  });

  function handleConfirm() {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError(t("changePassword.required"));
      return;
    }
    if (!PasswordSchema.safeParse(newPassword).success) {
      setError(t("changePassword.policy"));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t("changePassword.mismatch"));
      return;
    }
    setError(null);
    mutation.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("changePassword.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 px-6 pb-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="old-password">
              <span className="text-destructive-foreground">*</span>{" "}
              {t("changePassword.oldPassword")}
            </Label>
            <Input
              id="old-password"
              type="password"
              value={oldPassword}
              autoComplete="current-password"
              onChange={(event) => setOldPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-password">
              <span className="text-destructive-foreground">*</span>{" "}
              {t("changePassword.newPassword")}
            </Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="confirm-password">
              <span className="text-destructive-foreground">*</span>{" "}
              {t("changePassword.confirmPassword")}
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">{t("changePassword.policy")}</p>
          {error ? <p className="text-sm text-destructive-foreground">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("changePassword.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={mutation.isPending}>
            {t("changePassword.confirm")}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
